import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { useDialogFocus } from "@hybrid-iptv/ui";
import { usePlayerStore } from "./playerStore.js";
import { showRemoteNote } from "../remote/remoteNote.js";
import { installInputWatch, lastInput, noteInput, sleepDecision, useSleep, WARN_MS } from "./sleepTimer.js";

/**
 * The sleep timer's ticker and its dialog (player/sleepTimer.ts). Mounted once, in the app
 * shell, so it runs whatever screen is up. It renders nothing until a warning is due.
 *
 * Back and Close never stop playback: on the sleep warning they add 15 minutes (the viewer
 * is clearly awake), on "Still watching?" they keep watching. Only "Stop now", or no answer,
 * or the clock running out, stops it.
 *
 * It is drawn on <body>, with the app's popup markup (the exit question's): in full screen
 * everything inside .app is hidden (html.player-fullscreen .app), and a dialog mounted in the
 * shell was there, unseen and unfocusable, on the box (2026-10-12).
 */
type Ask = null | "sleep" | "idle";

const NOTE_SLEEP = "Stopped by the sleep timer";
const NOTE_IDLE = "Stopped: nobody was watching";

export function SleepGuard() {
  const [ask, setAsk] = useState<Ask>(null);
  const [remaining, setRemaining] = useState(0);
  const askRef = useRef<Ask>(null);
  const deadlineRef = useRef(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  askRef.current = ask;

  useEffect(() => installInputWatch(), []);

  const stop = useCallback((note: string) => {
    askRef.current = null;
    setAsk(null);
    useSleep.getState().setSleep(null);
    usePlayerStore.getState().close();
    showRemoteNote(note);
  }, []);

  const keepOn = useCallback(() => {
    const current = askRef.current;
    askRef.current = null;
    setAsk(null);
    if (current === "sleep") useSleep.getState().extend(15);
    else noteInput();
  }, []);

  useDialogFocus({ open: ask !== null, onClose: keepOn, rootRef: dialogRef });

  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      const { source, status } = usePlayerStore.getState();
      const playing = source !== null && status === "playing";
      const { endsAt, idleHours } = useSleep.getState();
      const open = askRef.current;

      // Whatever was playing is gone (another screen, an error): nothing left to ask about.
      if (open && source === null) {
        askRef.current = null;
        setAsk(null);
        return;
      }
      if (open === "sleep") {
        if (endsAt === null || endsAt - now > WARN_MS) {
          // Cleared or extended from elsewhere (a remote button): the question is moot.
          askRef.current = null;
          setAsk(null);
          return;
        }
        if (now >= endsAt) {
          stop(NOTE_SLEEP);
          return;
        }
        setRemaining(Math.ceil((endsAt - now) / 1000));
        return;
      }
      if (open === "idle") {
        if (now >= deadlineRef.current) {
          stop(NOTE_IDLE);
          return;
        }
        setRemaining(Math.ceil((deadlineRef.current - now) / 1000));
        return;
      }

      // A timer that ran out with nothing playing is not left to cut off the next film.
      if (endsAt !== null && now >= endsAt && !playing) {
        useSleep.getState().setSleep(null);
        return;
      }
      const decision = sleepDecision({ now, endsAt, lastInputAt: lastInput(), idleHours, playing });
      if (decision === "stopSleep") {
        stop(NOTE_SLEEP);
      } else if (decision === "warnSleep") {
        deadlineRef.current = endsAt ?? now + WARN_MS;
        askRef.current = "sleep";
        setRemaining(Math.max(0, Math.ceil((deadlineRef.current - now) / 1000)));
        setAsk("sleep");
      } else if (decision === "warnIdle") {
        deadlineRef.current = now + WARN_MS;
        askRef.current = "idle";
        setRemaining(Math.ceil(WARN_MS / 1000));
        setAsk("idle");
      }
    };
    // Every 5 s to watch; every second while a question is open, so its count is true.
    const timer = window.setInterval(tick, ask ? 1000 : 5000);
    return () => window.clearInterval(timer);
  }, [ask, stop]);

  if (ask === null) return null;
  const sleep = ask === "sleep";

  // Left/Right between the two buttons: a TV remote's D-pad, not Tab.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    const buttons = Array.from(dialogRef.current?.querySelectorAll("button") ?? []);
    if (buttons.length === 0) return;
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const step = event.key === "ArrowRight" ? 1 : -1;
    buttons[(Math.max(at, 0) + step + buttons.length) % buttons.length]?.focus();
    event.preventDefault();
  };

  return createPortal(
    <div className="epg-program-detail-overlay exit-confirm-overlay">
      <div
        ref={dialogRef}
        className="epg-program-detail exit-confirm"
        role="alertdialog"
        aria-label={sleep ? "Sleep timer" : "Still watching?"}
        aria-modal="true"
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <h2 className="epg-detail-title">{sleep ? "Sleep timer" : "Still watching?"}</h2>
        <p className="pair-request__desc">Stopping in {remaining} s.</p>
        <div className="exit-confirm__actions">
          <button type="button" className="exit-confirm__stay" onClick={keepOn}>
            {sleep ? "+15 minutes" : "Keep watching"}
          </button>
          <button type="button" className="exit-confirm__leave" onClick={() => stop(sleep ? NOTE_SLEEP : NOTE_IDLE)}>
            Stop now
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
